import React from 'react';
import { Box, MenuItem, TextField, Stack, Alert } from '@mui/material';
import { IRatingForm } from '../../Interfaces/IRatingForm';
import { ILegalTransactionState } from '../../Interfaces/ILegalTransactionState';
import {
  applyTriggerTypeChange,
  triggerReferenceHelperText,
  triggerSaveProblem,
} from '../../services/ratingTrigger.service';
import {
  ITriggerValue,
  TriggerType,
} from '../../services/ratingTrigger.service';

// Die Regeln zum Versandzeitpunkt liegen im Service, damit sie ohne Rendering
// prüfbar sind. Typen hier weiterhin exportiert — sie werden auch im
// Vorgangs-Tab verwendet.
export type { ITriggerValue, TriggerType };

interface IProps {
  value: ITriggerValue;
  onChange: (v: ITriggerValue) => void;
  ratingForms: IRatingForm[];
  legalTransactionStates: ILegalTransactionState[];
  // Konkrete Instanzen aus dem Vorgang
  phases: Array<{ id: number; label: string }>;
  requirements: Array<{ id: number; label: string }>;
  /** Maßnahmen nach Phasen gruppiert — gleiche Darstellung wie bei der
   *  Automatisierung im Vorgangsmanager (Phase als Überschrift, Maßnahmen
   *  eingerückt darunter). Wenn gesetzt, ersetzt es die flache Liste. */
  requirementGroups?: Array<{
    phaseLabel: string;
    requirements: Array<{ id: number; label: string }>;
  }>;
  disabled?: boolean;
  /** Bogen-Auswahl mitrendern. Wenn die Auswahl in einem eigenen Block
   *  darueber steht, hier ausblenden (default: true). */
  showFormSelect?: boolean;
}

export const RatingFormTriggerEditor: React.FC<IProps> = ({
  value,
  onChange,
  ratingForms,
  legalTransactionStates,
  phases,
  requirements,
  requirementGroups,
  disabled,
  showFormSelect = true,
}) => {
  const triggerType = value.TriggerType || 'state';

  const activeRatingForms = ratingForms.filter(
    f => f.IsActive !== false || f.idRatingForm === value.idRatingForm
  );

  // Fehlt die Referenz zum gewählten Auslöser, wird nie versendet. Das Feld
  // sagt es, damit der Zustand nicht erst beim Speichern auffällt.
  const problem = triggerSaveProblem(value);
  const referenceMissing = (field: keyof ITriggerValue): boolean =>
    value.idRatingForm !== null &&
    value.idRatingForm !== undefined &&
    (value[field] === null || value[field] === undefined);

  return (
    <Box>
      <Stack spacing={2}>
        {showFormSelect && (
          <TextField
            label='Bewertungsbogen'
            size='small'
            select
            fullWidth
            value={value.idRatingForm ?? ''}
            disabled={disabled}
            onChange={e =>
              onChange({
                ...value,
                idRatingForm:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
            helperText='Welcher Bewertungsbogen wird für diesen Vorgang verwendet?'
          >
            <MenuItem value=''>– Kein Bogen –</MenuItem>
            {activeRatingForms.map(f => (
              <MenuItem key={f.idRatingForm} value={f.idRatingForm}>
                {f.Title}
                {f.IsDefault ? ' (Standard)' : ''}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          label='Versand auslösen durch'
          size='small'
          select
          fullWidth
          value={triggerType}
          disabled={disabled || !value.idRatingForm}
          helperText='Legt fest, zu welchem Zeitpunkt der Bewertungsbogen automatisch an die Mandanten versendet wird.'
          onChange={e =>
            // Die Referenzen bleiben beim Wechsel erhalten und der Status wird
            // mit der Standardvorgabe belegt. Vorher leerte jeder Wechsel die
            // Refs — nach einer Runde durch alle vier Optionen war das
            // Statusfeld leer und der Versand nach dem Speichern lautlos aus
            // (QS #9, TF16).
            onChange(
              applyTriggerTypeChange(value, e.target.value as TriggerType)
            )
          }
        >
          <MenuItem value='never'>Nicht automatisch versenden</MenuItem>
          <MenuItem value='state'>
            Sobald der Vorgang einen bestimmten Status erreicht
          </MenuItem>
          <MenuItem value='phase'>
            Sobald eine bestimmte Phase abgeschlossen ist
          </MenuItem>
          <MenuItem value='requirement'>
            Sobald eine bestimmte Maßnahme erledigt ist
          </MenuItem>
        </TextField>

        {triggerType === 'state' && (
          <TextField
            label='Status'
            size='small'
            select
            fullWidth
            value={value.idLegalTransactionState ?? ''}
            disabled={disabled || !value.idRatingForm}
            onChange={e =>
              onChange({
                ...value,
                idLegalTransactionState:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
            error={referenceMissing('idLegalTransactionState')}
            helperText={
              referenceMissing('idLegalTransactionState')
                ? problem
                : triggerReferenceHelperText('state')
            }
          >
            <MenuItem value=''>
              – kein Status: kein automatischer Versand –
            </MenuItem>
            {legalTransactionStates.map(s => (
              <MenuItem
                key={s.idLegalTransactionState}
                value={s.idLegalTransactionState}
              >
                {s.LegalTransactionState}
              </MenuItem>
            ))}
          </TextField>
        )}

        {triggerType === 'phase' && (
          <TextField
            label='Phase'
            size='small'
            select
            fullWidth
            value={value.idLegalPhase ?? ''}
            disabled={disabled || !value.idRatingForm}
            onChange={e =>
              onChange({
                ...value,
                idLegalPhase:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
            error={referenceMissing('idLegalPhase')}
            helperText={
              referenceMissing('idLegalPhase')
                ? problem
                : triggerReferenceHelperText('phase')
            }
          >
            <MenuItem value=''>
              – keine Phase: kein automatischer Versand –
            </MenuItem>
            {phases.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {triggerType === 'requirement' && (
          <TextField
            label='Maßnahme/Voraussetzung'
            size='small'
            select
            fullWidth
            value={value.idLegalRequirement ?? ''}
            disabled={disabled || !value.idRatingForm}
            onChange={e =>
              onChange({
                ...value,
                idLegalRequirement:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
            error={referenceMissing('idLegalRequirement')}
            helperText={
              referenceMissing('idLegalRequirement')
                ? problem
                : triggerReferenceHelperText('requirement')
            }
          >
            <MenuItem value=''>
              – keine Maßnahme: kein automatischer Versand –
            </MenuItem>
            {requirementGroups
              ? requirementGroups.flatMap(group => [
                  <MenuItem
                    key={`phase-${group.phaseLabel}`}
                    value={`__phase_${group.phaseLabel}`}
                    disabled
                    sx={{ fontWeight: 600, opacity: 1 }}
                  >
                    {group.phaseLabel}
                  </MenuItem>,
                  ...group.requirements.map(r => (
                    <MenuItem key={r.id} value={r.id} sx={{ pl: 4 }}>
                      {r.label}
                    </MenuItem>
                  )),
                ])
              : requirements.map(r => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.label}
                  </MenuItem>
                ))}
          </TextField>
        )}

        {triggerType !== 'never' && !value.idRatingForm && (
          <Alert severity='info'>
            Bitte zuerst einen Bewertungsbogen auswählen.
          </Alert>
        )}
      </Stack>
    </Box>
  );
};
